import React, { useCallback, useEffect, useRef } from "react";
import "./Lightbox.scss";
import { Button } from '../../components/Button';
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIconWrapper } from "/react4xp/components/FontAwesomeIconWrapper";
import { useUpdateEffect } from './useUpdateEffect';

declare module 'react' {
	interface DialogHTMLAttributes<T> extends React.HTMLAttributes<T> {
		closedby?: 'any' | 'closerequest' | 'none';
	}
}

interface LightboxProps {
	image: string | null;
	onClose: () => void;
}

/*
 * 1. So the idea here is that when <Lightbox> is mounted, image prop is null, so it returns null.
 * 2. Then image prop is changed (by some parent).
 * 2.1 Which causes dialog to be returned.
 * 2.2 Which causes dialogRef to change and keydown eventListener to be added.
 * 2.3 Then useUpdateEffect stores previousFocusRef and calls dialogRef.showModal().
 * 3. When onClose is called
 * 3.1 If parent has setup onClose correctly, the image prop becomes null.
 * 3.2 Null is returned.
 * 3.3 Meaning dialog is unmounted and cleanup removes eventListener.
 * 3.4 And useUpdateEffect restores focus to previousFocusRef.
 */
export function Lightbox({ image, onClose }: LightboxProps): React.JSX.Element | null {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const previousFocusRef = useRef<HTMLElement | null>(null); // Store previous focused element

	// ─────────────────────────────────────────────────────────────────────────────────────────────
	// NOTE:
	//  When closedby is set to any, none of the code below is needed.
	//  However Safari has not implemented closedby yet :(
	// ─────────────────────────────────────────────────────────────────────────────────────────────
	const hasAddedListener = useRef(false); // Flag to ensure listener is added only once

	const handleKeyDown = useCallback((e: KeyboardEvent): void => {
		if ([
			'Enter',
			'Escape',
			'Space',
			'Tab',
		].includes(e.code)) {
			e.preventDefault(); // Don't scroll page on spacebar.
			onClose();
		}
	}, [onClose]);

	useEffect(() => {
		if (dialogRef.current && !hasAddedListener.current) {
			dialogRef.current.addEventListener("keydown", handleKeyDown, true); // Capturing
			hasAddedListener.current = true; // Mark listener as added
		}
		// Cleanup: Remove event listener on unmount
		return () => {
			if (dialogRef.current) {
				dialogRef.current.removeEventListener('keydown', handleKeyDown, true);
				hasAddedListener.current = false; // Reset to allow re-adding if ref changes
			}
		};
	}, [dialogRef.current]);
	// ─────────────────────────────────────────────────────────────────────────────────────────────

	useUpdateEffect(() => {
		if (image) {
			previousFocusRef.current = document.activeElement as HTMLElement; // Store focus
			dialogRef.current?.showModal(); // Open modal

			// Safari-safe focus: wait two frames
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					closeButtonRef.current?.focus();
				});
			});
		} else {
			previousFocusRef.current?.focus(); // Restore focus to previous element
		}
	}, [image]); // This will only run when image is updated, not on mount.

	if (!image) return null; // Prevent rendering if no image

	return (
		<dialog
			className="lightbox"
			closedby="any"
			onClick={onClose}
			ref={dialogRef}
		>
			<div className="lightbox__container-wrapper">
				<div
					className="lightbox__container-wrapper__container"
				>
					<img
						alt="Bilde av stoff"
						className="lightbox__container-wrapper__container__image"
						src={image}
					/>
					<Button
						autoFocus // Yes, Frisk button does pass on ...rest :)
						className="lightbox__container-wrapper__container__close-btn"

						// Not needed when closedby is any, but Safari doesn't support that :(
						onBlur={onClose}

						onClick={(e) => {
							e.stopPropagation(); // Stop event from bubbling to <dialog>
							onClose(); // Close the lightbox
						}}
						ref={closeButtonRef}
						size="large"
						variant="icon-filled"
					>
						<FontAwesomeIconWrapper fixedWidth={true} icon={faXmark} />
					</Button>
				</div>
			</div>
		</dialog>
	);
}
